import { apiTransport } from './api-client';
import type { CompanyHoliday, NewShift, ScheduleCell, Shift } from './operations';

/**
 * Scheduling, against the API.
 *
 * The grid the planner edits is a *join*, not a table: every active employee
 * crossed with every day of the week, with an assignment where one exists.
 * The server returns only the assignments — an unassigned cell is the absence
 * of a row — so the adapter builds the cross product from the directory and
 * lays the assignments over it. An "unassigned" cell is exactly that, and is
 * labelled so; the prototype guessed rest days from the calendar, and a guess
 * rendered as a roster is how somebody plans a week around a shift nobody
 * gave them.
 *
 * The settled-day rule lives on the server (§ bulk-assign): a day with
 * recorded attendance cannot be reassigned, whatever a client sends. The grid
 * locks exactly those days, from the server's own list of them — not from
 * "published" (a plan employees can see, still editable) and not from "in the
 * past" (a day nobody clocked into is not evidence of anything).
 */

interface ApiShiftTemplate {
  id: string;
  code: string;
  name: string;
  startMinutes: number;
  endMinutes: number;
  breakMinutes: number;
  graceMinutes: number;
  isActive: boolean;
}

interface ApiScheduleAssignment {
  id: string;
  employeeId: string;
  employee: { id: string; fullName: string; employeeNumber: string } | null;
  date: string;
  kind: string;
  shiftTemplateId: string | null;
  shiftTemplate: { code: string; name: string } | null;
  publishedAt: string | null;
}

interface ApiHoliday {
  id: string;
  date: string;
  name: string;
}

const minutesToTime = (minutes: number) =>
  `${String(Math.floor(minutes / 60)).padStart(2, '0')}:${String(minutes % 60).padStart(2, '0')}`;

function toShift(row: ApiShiftTemplate): Shift {
  return {
    id: row.id,
    code: row.code,
    name: row.name,
    start: minutesToTime(row.startMinutes),
    end: minutesToTime(row.endMinutes),
    breakMinutes: row.breakMinutes,
    gracePeriodMinutes: row.graceMinutes,
    // Stored as minutes so a night shift is expressible as end < start.
    nightShift: row.endMinutes < row.startMinutes,
    active: row.isActive,
  };
}

export const apiScheduleService = {
  async holidays(): Promise<CompanyHoliday[]> {
    const rows = await apiTransport().get<(ApiHoliday & { workLocationId?: string | null })[]>(
      '/api/v1/holidays',
      { query: { limit: 200 } },
    );
    // A local holiday tied to one site is not a company-wide day off.
    return rows
      .filter((row) => !row.workLocationId)
      .map((row) => ({ date: row.date.slice(0, 10), name: row.name }));
  },

  async shifts(): Promise<Shift[]> {
    const rows = await apiTransport().get<ApiShiftTemplate[]>('/api/v1/shifts');
    return rows.map(toShift);
  },

  async grid(from: string, to: string, departmentId?: string): Promise<ScheduleCell[]> {
    const [assignments, settledDays, holidays, employees] = await Promise.all([
      apiTransport().get<ApiScheduleAssignment[]>('/api/v1/schedules', { query: { from, to } }),
      apiTransport().get<{ employeeId: string; date: string }[]>('/api/v1/schedules/settled', {
        query: { from, to },
      }),
      apiTransport().get<ApiHoliday[]>('/api/v1/holidays'),
      apiTransport().get<
        {
          id: string;
          fullName: string;
          department: { id: string; name: string } | null;
          employmentStatus: string;
          dateHired: string;
          lastWorkingDay: string | null;
        }[]
      >('/api/v1/schedules/roster', { query: { from, to } }),
    ]);

    const settled = new Set(settledDays.map((row) => `${row.employeeId}:${row.date}`));
    const assignmentByKey = new Map(
      assignments.map((row) => [`${row.employeeId}:${row.date.slice(0, 10)}`, row]),
    );
    const holidayDates = new Set(holidays.map((row) => row.date.slice(0, 10)));

    /*
     * Only people employed during the week — the server's roster. Somebody
     * starting on 16 November was listed in every October week, as though
     * they could be rostered before their first day; a start mid-week shows
     * the days before it greyed and closed, and a last working day mid-week
     * the days after it ("Left"). The last day itself is a working day.
     */
    const staff = employees.filter(
      (employee) => !departmentId || employee.department?.id === departmentId,
    );

    const dates: string[] = [];
    for (
      let cursor = new Date(`${from}T00:00:00Z`);
      cursor <= new Date(`${to}T00:00:00Z`);
      cursor.setUTCDate(cursor.getUTCDate() + 1)
    ) {
      dates.push(cursor.toISOString().slice(0, 10));
    }

    const cells: ScheduleCell[] = [];

    for (const employee of staff) {
      for (const date of dates) {
        const assignment = assignmentByKey.get(`${employee.id}:${date}`) ?? null;

        const kind: ScheduleCell['kind'] = holidayDates.has(date)
          ? 'holiday'
          : assignment === null
            ? 'unassigned'
            : assignment.kind === 'rest_day'
              ? 'rest_day'
              : assignment.kind === 'holiday'
                ? 'holiday'
                : 'workday';

        cells.push({
          employeeId: employee.id,
          employeeName: employee.fullName,
          departmentName: employee.department?.name ?? '',
          date,
          shiftId: kind === 'workday' ? (assignment?.shiftTemplateId ?? null) : null,
          shiftName: kind === 'workday' ? (assignment?.shiftTemplate?.name ?? null) : null,
          kind,
          assigned: assignment !== null,
          published: assignment?.publishedAt != null,
          settled: settled.has(`${employee.id}:${date}`),
          startsOn:
            employee.dateHired && date < employee.dateHired.slice(0, 10)
              ? employee.dateHired.slice(0, 10)
              : null,
          leftAfter:
            employee.lastWorkingDay && date > employee.lastWorkingDay
              ? employee.lastWorkingDay
              : null,
        });
      }
    }

    return cells;
  },

  /**
   * Assign a shift (or clear to a rest day) across selected cells.
   *
   * The signature keeps the mock's actor parameter so the selector stays
   * cast-free; who acted is established by the server from the session.
   */
  async assign(
    employeeIds: string[],
    dates: string[],
    shiftId: string | null,
    _actor: unknown,
  ): Promise<{ assigned: number; skipped: number }> {
    const result = await apiTransport().post<{
      created: number;
      skipped: number;
      skippedSettled: number;
      skippedNotStarted?: number;
      skippedLeft?: number;
    }>('/api/v1/schedules/bulk-assign', {
      employeeIds,
      dates,
      shiftTemplateId: shiftId ?? undefined,
      kind: shiftId === null ? 'rest_day' : 'work',
      /*
       * The server protects a published day behind this flag. The planner
       * asks before sending it — its confirmation names how many selected
       * days employees already see — so by the time this runs the person has
       * chosen to change a published schedule.
       */
      overwriteExisting: true,
    });

    return {
      assigned: result.created,
      skipped:
        result.skipped +
        result.skippedSettled +
        (result.skippedNotStarted ?? 0) +
        (result.skippedLeft ?? 0),
    };
  },

  /**
   * Publish a range to employees, or withdraw it back to draft.
   *
   * `employeeIds` narrows to the rows on screen (a department filter); the
   * server intersects it with the caller's scope either way. Withdrawing
   * leaves days with recorded attendance published — the schedule somebody
   * worked to is part of the record — and reports how many it kept.
   */
  async setPublished(
    from: string,
    to: string,
    published: boolean,
    employeeIds?: string[],
  ): Promise<{ changed: number; skippedSettled: number }> {
    const body = { from, to, ...(employeeIds ? { employeeIds } : {}) };
    if (published) {
      const result = await apiTransport().post<{ published: number }>(
        '/api/v1/schedules/publish',
        body,
      );
      return { changed: result.published, skippedSettled: 0 };
    }
    const result = await apiTransport().post<{ unpublished: number; skippedSettled: number }>(
      '/api/v1/schedules/unpublish',
      body,
    );
    return { changed: result.unpublished, skippedSettled: result.skippedSettled };
  },

  async createShift(input: NewShift): Promise<Shift> {
    const toMinutes = (time: string) => {
      const [hours, minutes] = time.split(':').map(Number);
      return hours! * 60 + minutes!;
    };
    const row = await apiTransport().post<ApiShiftTemplate>('/api/v1/shifts', {
      code: input.code,
      name: input.name,
      startMinutes: toMinutes(input.start),
      endMinutes: toMinutes(input.end),
      breakMinutes: input.breakMinutes,
      graceMinutes: input.gracePeriodMinutes,
    });
    return toShift(row);
  },
};
